import { t } from 'i18next';

function unsafeTranslation(key: any, params: Record<string, unknown>) {
  return t(key, params);
}

export function length(
  stringLength: number,
  { lengthMsg = 'validators.operational.length.equals', noLengthParam = 'validators.operational.length.noParam' } = {}
) {
  return (value: any) => {
    if (value.length === undefined) {
      return unsafeTranslation(noLengthParam, { length: stringLength });
    }

    if (value.length !== stringLength) {
      return unsafeTranslation(lengthMsg, { length: stringLength });
    }

    return true;
  };
}

export function maxLength(
  stringLength: number,
  { lengthMsg = 'validators.operational.length.max', noLengthParam = 'validators.operational.length.noParam' } = {}
) {
  return (value: any) => {
    if (value?.length === undefined) {
      return unsafeTranslation(noLengthParam, { length: stringLength });
    }

    if (value.length > stringLength) {
      return unsafeTranslation(lengthMsg, { length: stringLength });
    }

    return true;
  };
}

export function minLength(
  stringLength: number,
  { lengthMsg = 'validators.operational.length.min', noLengthParam = 'validators.operational.length.noParam' } = {}
) {
  return (value: any) => {
    if (value.length === undefined) {
      return unsafeTranslation(noLengthParam, { length: stringLength });
    }

    if (value.length < stringLength) {
      return unsafeTranslation(lengthMsg, { length: stringLength });
    }

    return true;
  };
}

export function greaterThan(
  minimumValue: number | string,
  { minValue = 'validators.operational.compare.gt', invalidType = 'validators.operational.compare.invalid' } = {}
) {
  return (value: any) => {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return unsafeTranslation(invalidType, { value: minimumValue });
    }

    if (value <= minimumValue) {
      return unsafeTranslation(minValue, { value: minimumValue });
    }

    return true;
  };
}

export function greaterOrEqualThan(
  minimumValue: number | string,
  { minValue = 'validators.operational.compare.gte', invalidType = 'validators.operational.compare.invalid' } = {}
) {
  return (value: any) => {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return unsafeTranslation(invalidType, { value: minimumValue });
    }

    if (value < minimumValue) {
      return unsafeTranslation(minValue, { value: minimumValue });
    }

    return true;
  };
}

export function lessThan(
  maximumValue: number | string,
  { maxValue = 'validators.operational.compare.lt', invalidType = 'validators.operational.compare.invalid' } = {}
) {
  return (value: any) => {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return unsafeTranslation(invalidType, { value: maximumValue });
    }

    if (value >= maximumValue) {
      return unsafeTranslation(maxValue, { value: maximumValue });
    }

    return true;
  };
}

export function lessOrEqualThan(
  maximumValue: number | string,
  { maxValue = 'validators.operational.compare.lte', invalidType = 'validators.operational.compare.invalid' } = {}
) {
  return (value: any) => {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return unsafeTranslation(invalidType, { value: maximumValue });
    }

    if (value > maximumValue) {
      return unsafeTranslation(maxValue, { value: maximumValue });
    }

    return true;
  };
}

export function equals(equalValue: any, { eqValue = 'validators.operational.compare.eq' } = {}) {
  return (value: any) => {
    if (value !== equalValue) {
      return unsafeTranslation(eqValue, { value: equalValue });
    }

    return true;
  };
}

export function notEquals(notEqualsValue: any, { neqValue = 'validators.operational.compare.neq' } = {}) {
  return (value: any) => {
    if (value === notEqualsValue) {
      return unsafeTranslation(neqValue, { value: notEqualsValue });
    }

    return true;
  };
}

export function fileExtension(extensions: string[], { ext = 'validators.operational.file.ext' } = {}) {
  return (value: File) => {
    if (!value) return true;
    const regex = new RegExp(`(${extensions.map((e) => `\\${e}`).join('|')})$`, 'i');
    if (!regex.exec(value.name)) {
      return unsafeTranslation(ext, { extensions: extensions.join(', ') });
    }
    return true;
  };
}

export function fileMaxSize(size: number, unit: 'KB' | 'MB' | 'GB', { maxSize = 'validators.operational.file.maxSize' } = {}) {
  return (value: File) => {
    if (!value) return true;
    const unitMap = { KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };
    if (value.size > size * unitMap[unit]) {
      return unsafeTranslation(maxSize, { maxSize: size, unit });
    }
    return true;
  };
}
